import { Injectable } from "@angular/core";
import { CanActivate } from "@angular/router";
import { KeycloakService } from "keycloak-angular";
import { ConfigService } from "../services/config/config.service";
import { UtilitiesService } from "../services/utilities.service";

/**
 * A route guard that checks if the user is authenticated via Keycloak.
 * It ensures that the user is logged in before allowing access to a protected route.
 * The guard checks if the user is authenticated and if not, it triggers the login process.
 * If the realm name is found in the URL, the user is redirected to a specified route after logging in.
 */
@Injectable({
  providedIn: "root",
})
export class KeycloakGuard implements CanActivate {
  tokenData: any;

  /**
   * Creates an instance of the KeycloakGuard.
   * @param {KeycloakService} keycloakService - The service that handles Keycloak authentication.
   * @param {UtilitiesService} utilitiesService - A utility service for extracting information from URLs.
   */
  constructor(
    private keycloakService: KeycloakService,
    private configService: ConfigService,
    private utilitiesService: UtilitiesService
  ) {}

  /**
   * CanActivate guard method that checks if the user is logged in before accessing a route.
   *
   * It retrieves the realm from the URL using the `UtilitiesService`, and checks if the user is authenticated
   * through the Keycloak service. If not authenticated, it triggers the Keycloak login flow and redirects to the
   * project admin page. If the user is authenticated, it allows access to the route.
   *
   */
  async canActivate(): Promise<boolean> {
    const realmName = this.utilitiesService.extractRealmFromUrl();
    console.log("realmName", realmName);
    if (!realmName) return true;

    // Check if the user is logged in
    const isAuthenticated = await this.keycloakService.isLoggedIn();
    console.log("isAuthenticated", isAuthenticated);
    if (!isAuthenticated) {
      if (realmName) {
        await this.keycloakService.login({
          redirectUri:
            location.href === location.origin ||
            location.href === `${location.origin}/`
              ? this.configService.buildAppUrl("user/project")
              : location.href,
        });
      }
      return false;
    }

    return true;
  }
}
